import React, { useEffect } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import InstagramIcon from '@mui/icons-material/Instagram';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import StorefrontIcon from '@mui/icons-material/Storefront';

import bgImage from '../assets/background1.jpg'; // Adjust path if needed

// Framer Motion animation configs
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.5,
      staggerChildren: 0.08,
    },
  },
};

const textVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
};

const iconVariants = {
  initial: { opacity: 0, y: 50, scale: 0.7, rotate: -20 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    rotate: 0,
    transition: { type: 'spring', stiffness: 200, damping: 10 },
  },
  hover: {
    scale: 1.2,
    rotate: 5,
    boxShadow: '0px 0px 15px rgba(255,255,255,0.8)',
    transition: { duration: 0.3 },
  },
};

const HomePage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/explore');
    }, 5000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const titleText = 'Product Review Hub'.split('');

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Overlay for darkening image if needed */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)', // Dark overlay
          zIndex: 1,
        }}
      />

      {/* Content */}
      <Box sx={{ zIndex: 2, px: 2 }}>
        {/* Animated Title */}
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              letterSpacing: 4,
              color: '#ffffff',
              textShadow: '0 4px 10px rgba(0,0,0,0.4)',
              userSelect: 'none',
              fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
            }}
          >
            {titleText.map((char, index) => (
              <motion.span
                key={index}
                variants={textVariants}
                style={{ display: 'inline-block' }}
              >
                {char === ' ' ? '\u00A0' : char}
              </motion.span>
            ))}
          </Typography>
        </motion.div>

        {/* Subtext */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 1 }}
          style={{ marginTop: 20 }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.9)',
              fontWeight: 300,
              textShadow: '0 1px 2px rgba(0,0,0,0.2)',
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            Discover, review, and share your product experiences
          </Typography>
        </motion.div>

        {/* Animated Icons */}
        <Stack direction="row" spacing={4} sx={{ mt: 6 }} justifyContent="center">
          {[
            { icon: InstagramIcon, color: '#E1306C' },
            { icon: ShoppingCartIcon, color: '#4CAF50' },
            { icon: FavoriteIcon, color: '#F44336' },
            { icon: LocalMallIcon, color: '#2196F3' },
            { icon: StorefrontIcon, color: '#FF9800' },
          ].map(({ icon: Icon, color }, idx) => (
            <motion.div
              key={idx}
              variants={iconVariants}
              initial="initial"
              animate="animate"
              whileHover="hover"
              transition={{ delay: 2.5 + idx * 0.15 }}
            >
              <Icon
                sx={{
                  fontSize: { xs: 38, sm: 50 },
                  color: '#ffffff',
                  background: color,
                  borderRadius: '50%',
                  padding: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                }}
              />
            </motion.div>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default HomePage;
